import * as t from "io-ts";
import { UUID as uuidCodec } from "io-ts-types/UUID";

import {
  updatePostBaseFieldsCodec,
  fundraiserUpdatePostResponseCodec,
} from "../codecs/entities";
import { slugCodec } from "../codecs/nonprofit";
import { nonNegativeIntegerFromStringCodec } from "../codecs/number";
import { HttpMethod } from "../helpers/http";

import { makeRouteSpec } from ".";

export const fundraiserIdentifiersTokenCodec = t.type({
  fundraiserIdentifier: t.union([uuidCodec, slugCodec]),
  nonprofitIdentifier: t.union([uuidCodec, slugCodec]),
});

const fundraiserUpdatePostIdentifiersCodec = t.intersection([
  fundraiserIdentifiersTokenCodec,
  t.type({
    updatePostIdentifier: uuidCodec,
  }),
]);

/**
 * For fetching a single update post the identifier may be either the full
 * UUID or the post's per-fundraiser shortId. Mutations (patch/delete) still
 * require the full UUID.
 */
const getFundraiserUpdatePostIdentifiersCodec = t.intersection([
  fundraiserIdentifiersTokenCodec,
  t.type({
    updatePostIdentifier: t.union([
      uuidCodec,
      nonNegativeIntegerFromStringCodec,
    ]),
  }),
]);

export const postFundraiserUpdatePostRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePost",
  method: HttpMethod.POST,
  authenticated: true,
  bodyCodec: updatePostBaseFieldsCodec,
  paramsCodec: t.type({}),
  responseBodyCodec: fundraiserUpdatePostResponseCodec,
  tokensCodec: fundraiserIdentifiersTokenCodec,
});

export const getFundraiserUpdatePostsRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePosts",
  method: HttpMethod.GET,
  authenticated: false,
  bodyCodec: t.type({}),
  paramsCodec: t.type({}),
  responseBodyCodec: t.array(fundraiserUpdatePostResponseCodec),
  tokensCodec: fundraiserIdentifiersTokenCodec,
});

export type FundraiserUpdatePost = t.TypeOf<
  typeof fundraiserUpdatePostResponseCodec
>;

export const getFundraiserUpdatePostRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePost/:updatePostIdentifier",
  method: HttpMethod.GET,
  authenticated: false,
  bodyCodec: t.type({}),
  paramsCodec: t.type({}),
  responseBodyCodec: fundraiserUpdatePostResponseCodec,
  tokensCodec: getFundraiserUpdatePostIdentifiersCodec,
});

export const patchFundraiserUpdatePostRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePost/:updatePostIdentifier",
  method: HttpMethod.PATCH,
  authenticated: true,
  bodyCodec: updatePostBaseFieldsCodec,
  paramsCodec: t.type({}),
  responseBodyCodec: fundraiserUpdatePostResponseCodec,
  tokensCodec: fundraiserUpdatePostIdentifiersCodec,
});

export const deleteFundraiserUpdatePostRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePost/:updatePostIdentifier",
  method: HttpMethod.DELETE,
  authenticated: true,
  bodyCodec: t.type({}),
  paramsCodec: t.type({}),
  responseBodyCodec: t.type({}),
  tokensCodec: fundraiserUpdatePostIdentifiersCodec,
});

export const likeFundraiserUpdatePostRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePost/:updatePostIdentifier/like",
  method: HttpMethod.POST,
  authenticated: true,
  bodyCodec: t.type({}),
  paramsCodec: t.type({}),
  responseBodyCodec: t.type({}),
  tokensCodec: fundraiserUpdatePostIdentifiersCodec,
});

export const unlikeFundraiserUpdatePostRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/updatePost/:updatePostIdentifier/like",
  method: HttpMethod.DELETE,
  authenticated: true,
  bodyCodec: t.type({}),
  paramsCodec: t.type({}),
  responseBodyCodec: t.type({}),
  tokensCodec: fundraiserUpdatePostIdentifiersCodec,
});
